import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Scales as ScalesSimple,
    GoogleLogo,
    ShieldCheck,
    Quotes,
    Books,
    Translate,
    MagnifyingGlass,
    FileText,
    Microphone,
    Buildings,
    Sparkle,
    Gavel,
    Lock,
    ArrowRight,
} from "@phosphor-icons/react";

export default function Landing() {
    return (
        <div className="min-h-screen parchment grain relative" data-testid="landing-page">
            {/* Top bar */}
            <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <ScalesSimple size={22} weight="duotone" className="text-primary" />
                        <div>
                            <div className="font-serif text-lg leading-none tracking-tight">CounselIQ</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase text-muted-foreground mt-1">Advocate Workspace</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <a href="#features" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3">Features</a>
                        <a href="#workflows" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3">Workflows</a>
                        <a href="#access" className="hidden md:inline text-sm text-muted-foreground hover:text-foreground transition-colors px-3">Access</a>
                        <Link to="/login" data-testid="landing-signin-btn">
                            <Button size="sm" className="rounded-full px-5 h-9">Sign in</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{
                        backgroundImage: "url(https://images.pexels.com/photos/17843099/pexels-photo-17843099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600)",
                        backgroundSize: "cover", backgroundPosition: "center", filter: "grayscale(0.5)",
                    }}
                />
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 md:py-28 relative">
                    <div className="rule-line pt-3 max-w-3xl">
                        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-3">Delhi · India</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            Research, draft,
                            <br />
                            <span className="italic">and practice</span>
                            <br />
                            at court speed.
                        </h1>
                    </div>
                    <p className="mt-6 max-w-xl text-base md:text-lg text-foreground/80 leading-relaxed">
                        A focused AI workspace for Indian litigators — page-cited document analysis, citation-first
                        research, bilingual <span className="font-deva">हिन्दी</span> + English voice, and
                        formally-structured drafting.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link to="/login" data-testid="hero-signin-btn">
                            <Button className="h-12 px-7 rounded-full text-base gap-2">
                                <GoogleLogo size={18} weight="bold" /> Continue with Google
                            </Button>
                        </Link>
                        <a href="#features" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                            Explore capabilities <ArrowRight size={14} />
                        </a>
                    </div>
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
                        <Stat n="14" l="Draft templates" />
                        <Stat n="2" l="Languages" />
                        <Stat n="OCR" l="Hindi + English" />
                        <Stat n="∞" l="Citations" />
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-20 md:py-24 bg-white/40 border-y border-border">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <div className="rule-line pt-3 mb-14 max-w-2xl">
                        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-2">Capabilities</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
                            Every tool a Delhi litigator needs, <span className="italic">on one page</span>.
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Feature
                            icon={<MagnifyingGlass size={22} weight="duotone" />}
                            title="Citation-first research"
                            text="AI-assisted research with strict citation schema — case law, statutes, jurisdiction notes, and contradiction flags. Pluggable to verified-web research engines."
                        />
                        <Feature
                            icon={<FileText size={22} weight="duotone" />}
                            title="Page-cited document chat"
                            text="Upload PDFs, judgments, paper books. Get answers with 'Source: page 143'. Hindi + English OCR for scanned bundles."
                        />
                        <Feature
                            icon={<Quotes size={22} weight="duotone" />}
                            title="Drafting with provenance"
                            text="14 draft types from notice to SLP. Every draft carries a provenance panel. Strict mode forbids hallucination. Redline between versions."
                        />
                        <Feature
                            icon={<Microphone size={22} weight="duotone" />}
                            title="Bilingual voice"
                            text='"इस bail order का summary बताओ" — speak in Hindi, English, or code-switched. Whisper STT + read-aloud TTS.'
                        />
                        <Feature
                            icon={<Buildings size={22} weight="duotone" />}
                            title="Chambers + RBAC"
                            text="Invite associates and paralegals to a chamber. Owner / Admin / Associate / Paralegal roles control access to shared matters."
                        />
                        <Feature
                            icon={<Translate size={22} weight="duotone" />}
                            title="Devanagari-aware"
                            text="हिन्दी and English handled natively across documents, OCR, search, drafting and exports — DOCX & PDF preserve script."
                        />
                        <Feature
                            icon={<Books size={22} weight="duotone" />}
                            title="Knowledge vault"
                            text="Save reusable arguments, templates, precedents and clauses, tagged by court, judge, statute and matter type."
                        />
                        <Feature
                            icon={<ShieldCheck size={22} weight="duotone" />}
                            title="Audit + privacy"
                            text="Every research query, document access, and draft logged. Files encrypted in transit. Designed for Indian advocate-client privilege."
                        />
                        <Feature
                            icon={<Gavel size={22} weight="duotone" />}
                            title="Indian courts native"
                            text="Supreme Court, Delhi HC, six Delhi district courts, tribunals — pre-configured. Practice areas, stage tracking, hearing dates."
                        />
                    </div>
                </div>
            </section>

            {/* Workflows */}
            <section id="workflows" className="py-20 md:py-24">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <div className="rule-line pt-3 mb-14 max-w-2xl">
                        <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-2">A day in chambers</div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
                            From <span className="italic">notice</span> to <span className="italic">submissions</span>.
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
                        <Workflow n="01" title="Upload the paper book" text="Drop a 200-page scanned bundle. Tesseract Hindi+English OCR runs in background. You get pages-with-citations the moment it's ready." />
                        <Workflow n="02" title="Build the chronology" text="One click — every dated event extracted, sourced to its document and page." />
                        <Workflow n="03" title="Research the law" text="Ask in Hindi or English. Get a structured memo with case law, citation chips, jurisdiction notes and contradictions flagged." />
                        <Workflow n="04" title="Draft the reply" text="Generate a reply to a notice, attaching matter context and the research memo. Provenance panel shows exactly what was used." />
                        <Workflow n="05" title="Compare versions" text="Edit. Save. Redline. Revert. The draft stays formally structured for filing." />
                        <Workflow n="06" title="Export and file" text="DOCX & PDF exports preserve citations. Read-aloud the final memo on your way to court." />
                    </div>
                </div>
            </section>

            {/* Access */}
            <section id="access" className="py-20 md:py-24 bg-white/40 border-y border-border">
                <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                        <Lock size={22} weight="duotone" />
                    </div>
                    <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-2">Invite-only access</div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
                        CounselIQ is currently a <span className="italic">closed beta</span>.
                    </h2>
                    <p className="text-foreground/80 max-w-2xl mx-auto leading-relaxed">
                        Sign-in is restricted to advocates approved by an admin. If you have been invited or your email
                        is in our allowlist, click below to continue with Google.
                    </p>
                    <div className="mt-6">
                        <Link to="/login" data-testid="access-signin-btn">
                            <Button className="h-11 px-6 rounded-full gap-2"><GoogleLogo size={18} weight="bold" /> Continue with Google</Button>
                        </Link>
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground">
                        Not invited yet? Ask the chambers admin to add your email.
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 max-w-7xl mx-auto px-5 lg:px-8 text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <ScalesSimple size={14} weight="duotone" className="text-primary" />
                    <span className="font-serif">CounselIQ</span>
                    <span>· Delhi · India</span>
                </div>
                <div className="flex items-center gap-3">
                    <span>AI-assisted. Advocate review required before filing.</span>
                </div>
            </footer>
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div className="border-l-2 pl-3" style={{ borderColor: "hsl(var(--gold))" }}>
            <div className="font-serif text-3xl md:text-4xl tracking-tight">{n}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{l}</div>
        </div>
    );
}

function Feature({ icon, title, text }) {
    return (
        <div className="border border-border rounded-md bg-white p-5 hover:border-primary/40 hover:shadow-sm transition-all" data-testid="landing-feature">
            <div className="text-primary mb-3">{icon}</div>
            <div className="font-serif text-xl leading-tight">{title}</div>
            <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{text}</p>
        </div>
    );
}

function Workflow({ n, title, text }) {
    return (
        <div className="border border-border rounded-md bg-white p-6 flex gap-4">
            <div className="font-serif text-3xl leading-none" style={{ color: "hsl(var(--gold) / 0.8)" }}>{n}</div>
            <div>
                <div className="font-serif text-xl leading-tight">{title}</div>
                <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{text}</p>
            </div>
        </div>
    );
}
